import { z } from 'zod'

/** PROMPT.md §5.1. Reported live over the websocket. */
export const connectionStatusSchema = z.enum([
  'disconnected',
  'connecting',
  'connected',
  /** Connected, but a keepalive was missed or a reconnect is in progress. */
  'degraded',
  /** Reconnect attempts are exhausted. No silent retry loop follows. */
  'unreachable',
])
export type ConnectionStatus = z.infer<typeof connectionStatusSchema>

export const connectionStateSchema = z.object({
  serverId: z.string(),
  status: connectionStatusSchema,
  /** Short German explanation for the current status, e.g. the reason a connect failed. */
  detail: z.string().nullable(),
  since: z.iso.datetime(),
  /** Reconnect attempts spent so far; reset on a successful connect. */
  attempts: z.number().int().nonnegative(),
})
export type ConnectionState = z.infer<typeof connectionStateSchema>

/**
 * Result of "Verbindung testen". The host-key cases are outcomes, not errors: an unknown key is
 * the normal first contact and needs the user's confirmation before anything is sent (§5.1).
 */
export const connectionTestResultSchema = z.discriminatedUnion('result', [
  z.object({
    result: z.literal('ok'),
    algorithm: z.string(),
    fingerprint: z.string(),
    latencyMs: z.number().int().nonnegative(),
    /** The server's SSH banner, when it sent one. */
    banner: z.string().nullable(),
  }),
  z.object({
    result: z.literal('host-key-unknown'),
    algorithm: z.string(),
    fingerprint: z.string(),
  }),
  z.object({
    result: z.literal('host-key-mismatch'),
    algorithm: z.string(),
    expected: z.string(),
    actual: z.string(),
  }),
  z.object({
    result: z.literal('failed'),
    code: z.string(),
    message: z.string(),
  }),
])
export type ConnectionTestResult = z.infer<typeof connectionTestResultSchema>
